import type {DiffSide, StaticDiffLine} from '@github-ui/diff-lines/conversation-types'
import {create} from '@github-ui/dotcom-schema/protobuf'
import type {ThreadPreviewPositioningResponse} from '@github-ui/dotcom-schema/types/pull_requests/v1/thread_preview_positioning'
import {ThreadPreviewPositioningResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/thread_preview_positioning'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useQuery} from '@github-ui/react-query'
import {createProtoPersister} from '@github-ui/react-query-persister'
import {useAnalytics} from '@github-ui/use-analytics'
import {useMemo} from 'react'

/**
 * Legacy (pre-proto) response shape for the `thread_preview_positioning` endpoint: a top-level JSON
 * object keyed by review-thread id. Test-only — production emits the canonical proto
 * `ThreadPreviewPositioningResponse`; tests author fixtures in this shape and encode them up to the
 * proto via `threadPreviewPositioningToProto` (`test-utils/thread-preview-positioning-proto`).
 */
export type LegacyThreadPreviewPositioning = {
  [threadId: number]: {
    diffLines: StaticDiffLine[]
    endDiffSide?: DiffSide
    endLine?: number | null
    isOutdated?: boolean
    path: string
    startDiffSide?: DiffSide
    startLine?: number | null
    originalDiffPathUri?: string | null
  }
}

export function useThreadPreviewPositioning(threadIds: number[]) {
  const pathname = usePageDataUrl(PageData.threadPreviewPositioning)
  const threadIdsParam = threadIds.join(',')
  const threadPreviewPositioningUrl = `${pathname}?thread_ids=${threadIdsParam}`
  // `optional: true` because this data-fetching hook can render in trees without an
  // `AnalyticsContext` (e.g. bare `renderHook` unit tests); the persister still reports there.
  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  // Schema-bound proto response persists to IndexedDB (validated against the schema, keyed by its
  // hash) so revisits paint the last-known thread positioning immediately while revalidating.
  const persister = useMemo(
    () => createProtoPersister({schema: ThreadPreviewPositioningResponseSchema, sendAnalyticsEvent}),
    [sendAnalyticsEvent],
  )

  return useQuery<ThreadPreviewPositioningResponse>({
    queryKey: [PageData.threadPreviewPositioning, threadPreviewPositioningUrl],
    queryFn: async (): Promise<ThreadPreviewPositioningResponse> => {
      if (!threadIds.length) {
        return create(ThreadPreviewPositioningResponseSchema, {threads: {}})
      }

      // The endpoint always renders proto3-JSON, which `queryFnFetch` decodes into the proto
      // message via the generated schema.
      return queryFnFetch(
        {
          queryDeps: {pathname, searchParams: [['thread_ids', threadIdsParam]]},
          routeId: 'pull_request',
          queryName: 'threadPreviewPositioning',
        },
        {schema: ThreadPreviewPositioningResponseSchema},
      )
    },
    staleTime: Infinity,
    persister,
  })
}
